import StructuredCatalogSearch from "./StructuredCatalogSearch";
import CardQualityBadge from '../component/CardQualityBadge';
import CatalogSearchTools from '../component/CatalogSearchTools';
import CharacteristicMatrix from '../component/CharacteristicMatrix';
import ProductCharacteristicEditor from "../component/ProductCharacteristicEditor";
import ComponentRowsSearchModal from "../modals/ComponentRowsSearchModal";
import PeriodRange from "../component/PeriodRange";
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    BarChart3,
    ChevronDown,
    ChevronUp,
    Cpu,
    LayoutGrid,
    ExternalLink,
    Search,
    SlidersHorizontal
} from "lucide-react";

import { productCharacteristics, catalogCard, catalogDictionary, catalogSettings, popularCatalogComponents, popularCatalogDetails, searchCatalog } from "../api/catalogSearchApi";
import { catalogPriceHistory, catalogPriceHistoryOffers, catalogPriceRequest, catalogPriceSnapshots, requestCatalogPrices } from "../api/catalogPricesApi";
import { getAuthHeaders, hasRole } from "../api/auth";
import useCatalogAssetAvailability from "../hooks/useCatalogAssetAvailability";
import styles from "./CatalogPrototypePage.module.css";
import { conditionLabel, mayUseVectorRetrieval } from "./CatalogSearchPage";

const SEARCH_EXAMPLE = "конденсатор керамический 10 мкФ не менее 25 В";
const PAGE_SIZE = 100;
const OFFER_PREVIEW_LIMIT = 10;
const SHOW_SUPPLIER_FILTERS = false;
const CATALOG = [
    { label: "Керамические конденсаторы", query: "Ceramic Capacitors", active: true },
    { label: "Танталовые конденсаторы", query: "Tantalum Capacitors" },
    { label: "Алюминиевые конденсаторы", query: "Aluminum Electrolytic Capacitors" },
    { label: "Плёночные конденсаторы", query: "Film Capacitors" },
    { label: "Резисторы", query: "Resistors" },
    { label: "Индуктивности", query: "Inductors, Coils, Chokes" },
    { label: "Диоды", query: "Diodes" },
    { label: "Транзисторы", query: "Transistors" },
    { label: "Разъёмы", query: "Connectors, Interconnects" }
];
const SUPPLIER_FILTERS = [
    { value: "mouser", label: "Mouser" },
    { value: "digikey", label: "DigiKey" },
    { value: "getchips", label: "GetChips" },
    { value: "chipdip", label: "Чип и Дип" },
    { value: "symmetron", label: "Симметрон" }
];
const PRICE_SUPPLIERS = [
    { source_code: "mouser-hk-logistics", supplier_name: "Mouser — Гонконг" },
    { source_code: "digikey", supplier_name: "DigiKey" },
    { source_code: "getchips", supplier_name: "GetChips" },
    { source_code: "chipdip", supplier_name: "Чип и Дип" },
    { source_code: "symmetron", supplier_name: "Симметрон" }
];
const money = value => `$${value.toFixed(4)}`;
const withSnapshot = (part, snapshot) => current => ({ ...current, [part]: snapshot });
const offerQuantity = offer => offer.stock_quantity ?? offer.requested_quantity;
const hasPriceAndQuantity = offer => Number(offer.price_usd) > 0 && Number(offerQuantity(offer)) > 0;

const groupProducts = items => {
    const grouped = new Map();
    for (const item of items || []) {
        const key = `${item.part_number || ""}\u0000${item.manufacturer || ""}`;
        const current = grouped.get(key);
        if (!current) {
            grouped.set(key, {
                ...item,
                suppliers: item.supplier ? [item.supplier] : [],
                categories_ru: item.categories_ru || [],
                parameters: item.parameters || []
            });
            continue;
        }
        if (item.supplier && !current.suppliers.includes(item.supplier)) current.suppliers.push(item.supplier);
        if ((!current.parameters || current.parameters.length === 0) && item.parameters?.length) current.parameters = item.parameters;
        if ((!current.categories_ru || current.categories_ru.length === 0) && item.categories_ru?.length) current.categories_ru = item.categories_ru;
        if (!current.product_url && item.product_url) current.product_url = item.product_url;
    }
    return [...grouped.values()];
};

const storedAssets = materials => (materials || []).map(material => ({
    ...material,
    assets: (material.assets || []).filter(asset => asset.stored)
})).filter(material => material.assets.length);

const supplierName = value => ({
    mouser: "Mouser",
    "mouser-hk-logistics": "Mouser — Гонконг",
    digikey: "DigiKey",
    getchips: "GetChips",
    chipdip: "Чип и Дип",
    symmetron: "Симметрон"
})[String(value).toLowerCase()] || value;
const supplierResultLabel = result => ({
    NO_OFFERS: "Предложений не найдено",
    NOT_REQUESTED: "Ещё не запрашивался",
    PROCESSING: "Поиск выполняется…",
    FAILED: "Ошибка запроса"
})[result.status] || (Number(result.offer_count) > 0 ? `Найдено предложений: ${result.offer_count}` : result.status);
const translatedCategory = product => {
    const name = product.annotation_category?.name_ru || product.categories_ru?.at(-1) || product.categories?.at(-1) || "Категория не указана";
    return name;
};
const translatedParameter = parameter => `${parameter.name_ru || parameter.name}: ${parameter.value_ru || parameter.raw}${parameter.provisional && parameter.origin !== "peer" ? " · Предложено LLM" : ""}`;
const visibleParameters = product => (product.parameters || []).filter(parameter => !["has_pdf_link", "has_image_link"].includes(parameter.code));
const needsCharacteristicDetails = product => (!product.annotation_category && !product.categories_ru?.length && !product.categories?.length)
    || !visibleParameters(product).length;
const isPackageParameter = parameter => {
    const text = `${parameter.code || ""} ${parameter.name || ""} ${parameter.name_ru || ""}`.toLowerCase();
    return /(^|[_\s])(package|case|housing)([_\s/]|$)/.test(text) || text.includes("корпус");
};
const parameterValue = (product, patterns) => {
    const parameter = (product.parameters || []).find(item => patterns.some(pattern => `${item.code} ${item.name}`.toLowerCase().includes(pattern)));
    return parameter ? (parameter.value_ru || parameter.raw || "—") : "—";
};
const hideCategoryCounts = nodes => nodes.map(node => ({...node, count: null, children: hideCategoryCounts(node.children || [])}));
const fallbackCategoryTree = CATALOG.map(category => ({ name: category.query, label: category.label, count: null, children: [] }));
const categoryNodeKey = path => path.join("\u0001");
const filterCategoryTree = (nodes, value) => {
    const term = value.trim().toLowerCase();
    if (!term) return nodes;
    return (nodes || []).flatMap(node => {
        const children = filterCategoryTree(node.children || [], value);
        if (`${node.label} ${node.name}`.toLowerCase().includes(term)) return [{ ...node }];
        return children.length ? [{ ...node, children }] : [];
    });
};
const findCategoryPath = (nodes, name, prefix = []) => {
    for (const node of nodes || []) {
        const path = [...prefix, node.name];
        if (node.name === name) return path;
        const found = findCategoryPath(node.children, name, path);
        if (found) return found;
    }
    return null;
};

function CategoryTreeNode({ node, path, selected, expanded, forceOpen, onToggle, onSelect }) {
    const query = node.query || node.name;
    const currentPath = [...path, query];
    const key = categoryNodeKey(currentPath);
    const hasChildren = Boolean(node.children?.length);
    const open = forceOpen || expanded.has(key);
    return <div className={styles.categoryNode}>
        <div className={styles.categoryRow} style={{ "--category-depth": path.length }}>
            {hasChildren ? <button className={styles.categoryToggle} type="button" aria-label={`${open ? "Свернуть" : "Развернуть"} ${node.label}`} onClick={() => onToggle(key)}>{open ? <ChevronDown size={14} /> : <ChevronDown className={styles.chevronClosed} size={14} />}</button> : <span className={styles.categoryLeaf} />}
            <button aria-label={node.label} className={selected === query ? styles.catalogActive : ""} type="button" onClick={() => onSelect({ query, label: node.label })}>
                <span>{node.label}</span>{node.count != null && <small>{node.count}</small>}
            </button>
        </div>
        {hasChildren && open && <div>{node.children.map(child => <CategoryTreeNode key={`${key}-${child.name}`} node={child} path={currentPath} selected={selected} expanded={expanded} forceOpen={forceOpen} onToggle={onToggle} onSelect={onSelect} />)}</div>}
    </div>;
}


// [[Catalog Prototype]] Category labels and counts come from the saved catalog dictionary.
function categoryPhoto(node) {
    const collectNames = current => [current?.label, current?.name, ...(current?.children || []).flatMap(collectNames)].filter(Boolean);
    const names = collectNames(node).join(" ").toLowerCase();
    if (/semiconductor|микросхем|полупровод|диод|транзистор/.test(names)) return "/category-images/semiconductors.jpg";
    if (/passive|пассив|capacitor|конденсатор|resistor|резистор|inductor|индуктив|резонатор/.test(names)) return "/category-images/passive-components.jpg";
    if (/measure|instrument|измер|осциллограф|мультиметр|генератор сигнал/.test(names)) return "/category-images/test-equipment.jpg";
    if (/connector|соединител|разъем|разъём|кабел|cable/.test(names)) return "/category-images/connectors-cables.jpg";
    if (/meter|прибор/.test(names)) return "/category-images/meters.jpg";
    return "/category-images/semiconductors.jpg";
}

function CategoryPhoto({ node }) {
    const { image_url: imageUrl, name, label } = node;
    const [src, setSrc] = useState(null);
    useEffect(() => {
        const fallback = categoryPhoto({ name, label });
        if (!imageUrl) { setSrc(fallback); return undefined; }
        let objectUrl;
        const controller = new AbortController();
        fetch(imageUrl, { headers: getAuthHeaders(), signal: controller.signal })
            .then(response => { if (!response.ok) throw new Error("image"); return response.blob(); })
            .then(blob => { objectUrl = URL.createObjectURL(blob); setSrc(objectUrl); })
            .catch(() => { if (!controller.signal.aborted) setSrc(fallback); });
        return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [imageUrl, name, label]);
    return <img className={styles.categoryPhoto} src={src || categoryPhoto({ name, label })} alt="" loading="lazy" />;
}

function CategoryDirectory({ tree, onSelect }) {
    const select = node => onSelect({ query: node.query || node.name, label: node.label || node.name });
    const entry = node => <li key={node.query || node.name}>
        <button type="button" onClick={() => select(node)}>{node.label || node.name}{node.count != null && <small>{node.count}</small>}</button>
    </li>;
    return <section className={styles.categoryDirectory} aria-label="Каталог по категориям">
        <h2>Каталог товаров</h2>
        <div className={styles.directoryGrid}>{tree.map(node => <section className={styles.directoryGroup} key={node.query || node.name}>
            <CategoryPhoto node={node} />
            <div><h3><button type="button" onClick={() => select(node)}>{node.label || node.name}</button>{node.count != null && <small>{node.count}</small>}</h3>
                <ul>{(node.children || []).map(entry)}</ul>
            </div>
        </section>)}</div>
        {!tree.length && <p>В каталоге пока нет категорий.</p>}
    </section>;
}

function ProductImage({ partNumber, materials }) {
    const asset = storedAssets(materials).flatMap(material => material.assets).find(item => item.kind === "image");
    const [state, setState] = useState({ source: null, url: null, failed: false });

    React.useEffect(() => {
        if (!asset?.url) {
            setState({ source: null, url: null, failed: false });
            return undefined;
        }
        const controller = new AbortController();
        let objectUrl;
        setState({ source: asset.url, url: null, failed: false });
        fetch(asset.url, { signal: controller.signal })
            .then(response => {
                if (!response.ok) throw new Error("image unavailable");
                return response.blob();
            })
            .then(blob => {
                if (controller.signal.aborted) return;
                objectUrl = URL.createObjectURL(blob);
                setState({ source: asset.url, url: objectUrl, failed: false });
            })
            .catch(() => {
                if (!controller.signal.aborted) setState({ source: asset.url, url: null, failed: true });
            });
        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [asset?.url]);

    if (!asset || state.failed) return <span className={styles.imagePlaceholder}>
        <span className={styles.placeholderIcon}><Cpu size={28} /></span>
        <strong>{state.failed ? "Фото недоступно" : "Нет фото"}</strong>
        <small>{state.failed ? "Не удалось открыть файл" : "Изображение пока не загружено"}</small>
    </span>;
    if (state.source !== asset.url || !state.url) return <span className={styles.imagePlaceholder}><span className={styles.loader} /><small>Загрузка</small></span>;
    return <img className={styles.productImage} src={state.url} alt={partNumber} />;
}

function DatasheetLink({ partNumber, materials }) {
    const asset = storedAssets(materials).flatMap(material => material.assets).find(item => item.kind === "datasheet");
    const [opening, setOpening] = useState(false);
    const [error, setError] = useState("");
    if (!asset?.url) return <span className={styles.noDatasheet}>Спецификация не загружена</span>;
    const open = async () => {
        const tab = window.open("about:blank", "_blank");
        if (!tab) {
            setError("Браузер заблокировал новую вкладку");
            return;
        }
        tab.opener = null;
        tab.document.title = `Спецификация ${partNumber}`;
        tab.document.body.textContent = "Загрузка спецификации…";
        setOpening(true);
        setError("");
        try {
            const response = await fetch(asset.url);
            if (!response.ok) throw new Error(`Не удалось открыть спецификацию: ${response.status}`);
            const url = URL.createObjectURL(await response.blob());
            if (!tab.closed) tab.location.replace(url);
            window.setTimeout(() => URL.revokeObjectURL(url), 120000);
        } catch (openError) {
            if (!tab.closed) tab.close();
            setError(openError.message || "Не удалось открыть спецификацию");
        } finally {
            setOpening(false);
        }
    };
    return <span className={styles.datasheetAction}>
        <button className={styles.datasheetLink} type="button" onClick={open} disabled={opening} aria-label={`Спецификация PDF ${partNumber}`}>
            <span>PDF</span>{opening ? "Открываю…" : "Спецификация"}
        </button>
        {error && <small role="status">{error}</small>}
    </span>;
}

export function withCharacteristicDisplay(product, annotation) {
    if (!annotation) return product;
    // V3 labels describe the evidence snapshot used for this search result.
    if (product.engine === "v3") return { ...product, card_status: annotation.card_status || product.card_status, verified: Boolean(product.verified && !product.has_provisional_values && !product.category_proposed) };
    const categories = [...new Set((annotation.donors || []).map(d => d.category).filter(Boolean))];
    return { ...product, card_status: annotation.card_status || product.card_status, category_proposed: !annotation.category_override && annotation.category_proposal?.status === "proposed", verified: annotation.verification?.verified ?? false, parameters: annotation.parameters || product.parameters,
        categories_ru: annotation.category_override?.name_ru ? [annotation.category_override.name_ru] : product.categories_ru?.length ? product.categories_ru : product.categories?.length ? product.categories : categories.length === 1 ? categories : annotation.category_proposal?.status==='proposed'&&annotation.category_proposal.name_ru ? [annotation.category_proposal.name_ru] : [] };
}
function ProductDetails({ product, materials, onAnnotation }) {
    const [showAllProps, setShowAllProps] = useState(false);
    const [annotation, setAnnotation] = useState(null);
    const [annotationError, setAnnotationError] = useState('');
    const [loadedAnnotationId, setLoadedAnnotationId] = useState(null);
    const missingDetails = needsCharacteristicDetails(product);
    useEffect(() => {
        let live = true; setAnnotation(null); setAnnotationError(''); setLoadedAnnotationId(null);
        if (product.id && hasRole('ADMIN') && missingDetails) productCharacteristics(product.id).then(data => {
            if (live) { setAnnotation(data); setLoadedAnnotationId(product.id); onAnnotation?.(product.id, data); }
        }).catch(() => { if (live) { setLoadedAnnotationId(product.id); setAnnotationError('Не удалось обновить характеристики карточки. Откройте «Все характеристики» для повторной загрузки.'); } });
        return () => { live = false; };
    }, [product.id, missingDetails, onAnnotation]);
    const currentAnnotation = loadedAnnotationId === product.id ? annotation : null;
    const displayedProduct = withCharacteristicDisplay(product, currentAnnotation);
    const allParameters = visibleParameters(displayedProduct);
    const detailsLoading = Boolean(product.id && hasRole('ADMIN') && loadedAnnotationId !== product.id && missingDetails);
    return <section className={styles.productDetails}>
        <div className={styles.detailVisual}>
            <ProductImage partNumber={product.part_number} materials={materials} />
            <small>Изображение приведено для справки. Точные параметры указаны в спецификации.</small>
        </div>
        <div className={styles.detailContent}>
            <h3>{product.part_number}</h3>
            {detailsLoading ? <p role="status">Загружаю категорию и характеристики…</p> : <CardQualityBadge product={displayedProduct} />}
            {!detailsLoading && <>
            <dl>
                <dt>Производитель</dt><dd>{product.manufacturer || "Не указан"}</dd>
                <dt>Номер производителя</dt><dd>{product.part_number}</dd>
                <dt>Описание</dt><dd>{product.description || "Описание не указано"}</dd>
                <dt>Категория</dt><dd>{translatedCategory(displayedProduct)}</dd>
                <dt>Корпус</dt><dd>{parameterValue(displayedProduct, ["package", "case"])}</dd>
                <dt>Тип монтажа</dt><dd>{parameterValue(displayedProduct, ["mounting", "монтаж"])}</dd>
                <dt>Спецификация</dt><dd><DatasheetLink partNumber={product.part_number} materials={materials} /></dd>
            </dl>
            {annotationError && <p role="status">{annotationError}</p>}
            <div className={styles.allParameters}>{allParameters.slice(0, 12).map(parameter => <span key={`${parameter.code}-${parameter.raw}`}>{translatedParameter(parameter)}</span>)}</div>
            </>}
            <button type="button" className={styles.allPropsButton} onClick={() => setShowAllProps(true)}>Все характеристики</button>
            {Object.keys(product.unverified_characteristics || {}).length > 0 && <details className={styles.legacyCharacteristics}>
                <summary>Сохранённые характеристики — не проверены поставщиком</summary>
                <dl>{Object.entries(product.unverified_characteristics).map(([name, value]) => <React.Fragment key={name}>
                    <dt>{name}</dt><dd>{value == null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                </React.Fragment>)}</dl>
            </details>}
        </div>
        {showAllProps && <div className={styles.modalOverlay} onClick={() => setShowAllProps(false)}>
            <div className={styles.modal} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true">
                <div className={styles.modalHead}><h3>Все характеристики — {product.part_number}</h3><button type="button" className={styles.modalClose} onClick={() => setShowAllProps(false)} aria-label="Закрыть">✕</button></div>
                <div className={styles.propsList}>
                    <p className={styles.propsDescription}>{product.description || "Описание не указано"}</p>
                    {product.id && hasRole('ADMIN') && <ProductCharacteristicEditor key={product.id} productId={product.id} onDataChange={data => { setAnnotation(data); setLoadedAnnotationId(product.id); onAnnotation?.(product.id, data); }} />}
                    {!(product.id && hasRole('ADMIN')) && <CharacteristicMatrix simple fields={Array.from(new Set(allParameters.map(p=>p.code))).map(code=>({...allParameters.find(p=>p.code===code),values:allParameters.filter(p=>p.code===code)}))} />}

                </div>
            </div>
        </div>}
    </section>;
}

const offerConditions = offer => {
    if (offer.remark) return offer.remark;
    const terms = [];
    if (Number(offer.minimum) > 0) terms.push(`От ${Number(offer.minimum).toLocaleString("ru-RU")} шт.`);
    if (Number(offer.step) > 1) terms.push(`кратно ${Number(offer.step).toLocaleString("ru-RU")} шт.`);
    return terms.join("; ") || "—";
};

const offerView = (offers, completeOnly, showAll) => {
    const completeOffers = offers.filter(hasPriceAndQuantity);
    const filteredOffers = completeOnly ? completeOffers : offers;
    const displayedOffers = showAll ? filteredOffers : filteredOffers.slice(0, OFFER_PREVIEW_LIMIT);
    return { filteredOffers, displayedOffers, hasHiddenOffers: displayedOffers.length < filteredOffers.length };
};

function OfferTable({ supplierResults = [], displayedOffers, ariaLabel }) {
    const priced = displayedOffers.filter(offer => offer.price_usd != null).map(offer => Number(offer.price_usd)).filter(Number.isFinite);
    const best = priced.length ? Math.min(...priced) : null;
    return <div className={styles.offerTable} role="table" aria-label={ariaLabel}>
        <div className={styles.offerHead} role="row"><span>Поставщик</span><span>В наличии</span><span>Цена / шт.</span><span>Условия цены</span><span>Срок поставки</span><span /></div>
        {displayedOffers.map((offer, index) => <div className={styles.offerRow} role="row" key={`${offer.id || "offer"}-${offer.source_code}-${offer.price_usd}-${index}`}>
            <strong>{supplierName(offer.supplier_name || offer.source_code)}</strong>
            <span>{offerQuantity(offer) == null ? "—" : `${Number(offerQuantity(offer)).toLocaleString("ru-RU")} шт.`}</span>
            <span className={Number(offer.price_usd) === best ? styles.bestPrice : ""}>{offer.price_usd == null ? "—" : `$${Number(offer.price_usd).toFixed(4)}`}</span>
            <span className={styles.offerConditions}>{offerConditions(offer)}</span>
            <span>{offer.lead_time || offer.availability || "—"}</span>
            {offer.product_url ? <a className={styles.offerLink} href={offer.product_url} target="_blank" rel="noopener noreferrer">Открыть</a> : <span />}
        </div>)}
        {supplierResults.filter(item => Number(item.offer_count || 0) === 0).map(item => <div className={styles.supplierResultRow} role="row" key={item.source_code}>
            <strong>{supplierName(item.source_code)}</strong>
            <span className={item.status === "FAILED" ? styles.error : ""}>{supplierResultLabel(item)}</span>
            <span />
            <span />
            <span>{item.status === "FAILED" && item.error ? item.error : "—"}</span>
            <span />
        </div>)}
    </div>;
}

export function PriceHistory({ partNumber, history = [] }) {
    const [period, setPeriod] = useState([0, null]);
    const [detail, setDetail] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [completeOnly, setCompleteOnly] = useState(true);
    useLayoutEffect(() => { setDetail(null); setShowAll(false); setCompleteOnly(true); setPeriod([0, null]); }, [partNumber]);
    const allPoints = history.map(item => ({
        date: item.price_day,
        value: Number(item.average_price_usd),
        minimum: item.minimum_price_usd == null ? null : Number(item.minimum_price_usd),
        count: Number(item.offer_count || 0)
    })).filter(item => item.date && Number.isFinite(item.value));
    const sortedPoints = allPoints.sort((a, b) => a.date.localeCompare(b.date));
    const dates = sortedPoints.map(item => item.date);
    const start = Math.min(period[0], Math.max(0, dates.length - 1));
    const end = Math.max(start, Math.min(period[1] ?? dates.length - 1, dates.length - 1));
    const pointsData = sortedPoints.slice(start, end + 1);
    if (!pointsData.length) return <section className={styles.history} aria-label={`История цены ${partNumber}`}>
        <div className={styles.historyHeader}><strong>История цен</strong><span>USD · по дням</span></div>
        <div className={styles.emptyHistory}>История предложений по этому артикулу пока не накоплена.</div>
    </section>;
    const values = pointsData.map(item => item.value);
    const width = 520, height = 126, pad = 12;
    const minimumValues = pointsData.map(item => item.minimum).filter(value => Number.isFinite(value) && value > 0);
    const low = Math.min(...values, ...minimumValues), high = Math.max(...values, ...minimumValues);
    const margin = Math.max((high - low) * 0.12, high * 0.02, 0.0001);
    const min = Math.max(0, low - margin), max = high + margin, spread = max - min;
    const points = values.map((value, index) => {
        const x = values.length === 1 ? width / 2 : pad + index * ((width - pad * 2) / (values.length - 1));
        const y = height - pad - ((value - min) / spread) * (height - pad * 2);
        return `${x},${y}`;
    }).join(" ");
    const minimumPoints = pointsData.map((item, index) => {
        if (!Number.isFinite(item.minimum) || item.minimum <= 0) return null;
        const x = values.length === 1 ? width / 2 : pad + index * ((width - pad * 2) / (values.length - 1));
        const y = height - pad - ((item.minimum - min) / spread) * (height - pad * 2);
        return `${x},${y}`;
    });
    const minimumPath = minimumPoints.map((point, index) => point ? `${index && minimumPoints[index - 1] ? "L" : "M"}${point}` : "").join(" ");
    const totalOffers = pointsData.reduce((sum, item) => sum + item.count, 0);
    const selectPoint = async item => {
        setShowAll(false);
        setCompleteOnly(true);
        setDetail({ ...item, loading: true, offers: [], error: "" });
        try {
            const offers = await catalogPriceHistoryOffers(partNumber, item.date);
            setDetail(current => current?.date === item.date ? { ...item, loading: false, offers, error: "" } : current);
        } catch (error) {
            setDetail(current => current?.date === item.date ? { ...item, loading: false, offers: [], error: error.message || "Не удалось загрузить предложения" } : current);
        }
    };
    return <section className={styles.history} aria-label={`История цены ${partNumber}`}>
        <div className={styles.historyHeader}>
            <div><strong>История цен</strong></div>
            <span>{pointsData[0].date} — {pointsData.at(-1).date} · {money(values.at(-1))}</span>
        </div>
        <PeriodRange dates={dates} value={period} onChange={value => { setPeriod(value); setDetail(null); }} />
        <div className={styles.chartLegend}><span>Средняя цена</span><span>Минимальная цена</span></div>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="График средней и минимальной цены предложений по дням">
            <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} className={styles.chartAxis} />
            <polyline points={points} className={styles.chartLine} />
            <path d={minimumPath} className={`${styles.chartLine} ${styles.minimumLine}`} />
            {minimumPoints.map((point, index) => {
                if (!point) return null;
                const [cx, cy] = point.split(",");
                const item = pointsData[index];
                const label = `${item.date} · минимальная цена $${item.minimum.toFixed(4)} · предложений: ${item.count}`;
                return <circle key={index} cx={cx} cy={cy} r="4" className={`${styles.chartDot} ${styles.minimumDot}`} tabIndex="0" role="button" aria-label={label}
                    onClick={() => selectPoint(item)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); selectPoint(item); } }}>
                    <title>{label}</title>
                </circle>;
            })}
            {points.split(" ").map((point, index) => {
                const [cx, cy] = point.split(",");
                const item = pointsData[index];
                const label = `${item.date} · ${money(item.value)} · предложений: ${item.count}`;
                return <circle key={index} cx={cx} cy={cy} r={detail?.date === item.date ? "6" : "4"} className={styles.chartDot} tabIndex="0" role="button" aria-label={label}
                    onClick={() => selectPoint(item)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); selectPoint(item); } }}>
                    <title>{label}</title>
                </circle>;
            })}
        </svg>
        <div className={styles.historyStats}><span>Мин. средняя {money(Math.min(...values))}</span><span>Макс. средняя {money(Math.max(...values))}</span><span>Предложений: {totalOffers}</span></div>
        {detail && <div className={styles.historyDetail}>
            <div className={styles.historyDetailHeader}>
                <div><strong>Предложения за {detail.date}</strong><span>Средняя цена: {money(detail.value)}{Number.isFinite(detail.minimum) && detail.minimum > 0 ? ` · Минимальная: $${detail.minimum.toFixed(4)}` : ""} · строк: {detail.count}</span></div>
                {!detail.loading && !detail.error && detail.offers.length > 0 && (() => {
                    const view = offerView(detail.offers, completeOnly, showAll);
                    return <div className={styles.priceHeadingActions}>
                        <button className={`${styles.refreshPrices} ${completeOnly ? styles.activeOfferFilter : ""}`} type="button" aria-pressed={completeOnly} onClick={() => { setCompleteOnly(current => !current); setShowAll(false); }}>С ценой и количеством</button>
                        {(showAll || view.hasHiddenOffers) && <button className={styles.refreshPrices} type="button" onClick={() => setShowAll(current => !current)}>{showAll ? "Показать топ-10" : `Показать все предложения (${view.filteredOffers.length})`}</button>}
                    </div>;
                })()}
            </div>
            {detail.loading ? <p>Загрузка предложений…</p> : detail.error ? <p className={styles.error}>{detail.error}</p> : detail.offers.length ? <OfferTable displayedOffers={offerView(detail.offers, completeOnly, showAll).displayedOffers} ariaLabel={`Предложения поставщиков за ${detail.date}`} /> : <p>Исходные предложения за этот день не найдены.</p>}
        </div>}
    </section>;
}

// History is read independently of today's prices and is shared by all manufacturers of the article.
export function ArticlePriceHistory({ partNumber, fallbackHistory = [], refreshKey = null }) {
    const [view, setView] = useState("chart");
    const [rowsVisited, setRowsVisited] = useState(false);
    const [state, setState] = useState({ part: null, history: null, loading: true, error: "" });
    const [revision, setRevision] = useState(0);
    useEffect(() => { setView("chart"); setRowsVisited(false); }, [partNumber]);
    useEffect(() => {
        let active = true;
        setState({ part: partNumber, history: null, loading: true, error: "" });
        catalogPriceHistory(partNumber).then(history => {
            if (active) setState({ part: partNumber, history, loading: false, error: "" });
        }).catch(() => {
            if (active) setState({ part: partNumber, history: null, loading: false, error: "Не удалось загрузить историю цен" });
        });
        return () => { active = false; };
    }, [partNumber, revision, refreshKey]);
    const current = state.part === partNumber ? state : { loading: true };
    return <section aria-label={`История артикула ${partNumber}`}>
        <p className={styles.searchExplanation}>История по артикулу {partNumber} · все производители</p>
        <div className={styles.priceHeadingActions} role="group" aria-label="Вид истории">
            <button className={styles.refreshPrices} type="button" aria-pressed={view === "chart"} onClick={() => setView("chart")}>График цен</button>
            <button className={styles.refreshPrices} type="button" aria-pressed={view === "rows"} onClick={() => { setRowsVisited(true); setView("rows"); }}>Запросы и предложения</button>
        </div>
        <div hidden={view !== "chart"}>
            {current.loading && <p role="status">Загружаю историю цен…</p>}
            {current.error && <p role="alert">{current.error} <button type="button" onClick={() => setRevision(n => n + 1)}>Повторить загрузку истории</button></p>}
            {(!current.loading || fallbackHistory.length > 0) && <PriceHistory partNumber={partNumber} history={current.history ?? fallbackHistory} />}
        </div>
        {rowsVisited && <div hidden={view !== "rows"}><ComponentRowsSearchModal key={partNumber} embedded partNumber={partNumber} /></div>}
    </section>;
}

function ProductPricePanel({ product, snapshot = {}, quantity, requestError, showAll, completeOnly, onToggleAll, onToggleComplete, onRequest }) {
    const [expandedProductKey, setExpandedProductKey] = useState(null);
    const tableId = useId();
    const productKey = `${product.id ?? ""}:${product.part_number}:${product.manufacturer ?? ""}`;
    const tableExpanded = expandedProductKey === productKey;
    const loading = snapshot._loaded !== true;
    const offers = snapshot.offers || [];
    const { filteredOffers, displayedOffers, hasHiddenOffers } = offerView(offers, completeOnly, showAll);
    const supplierResults = Array.isArray(snapshot.supplier_results) ? snapshot.supplier_results : [];
    const missingSources = Array.isArray(snapshot.missing_sources) ? snapshot.missing_sources : null;
    const canRequestSources = missingSources === null || missingSources.length > 0;
    return <>
        <ArticlePriceHistory partNumber={product.part_number} fallbackHistory={snapshot.history || []} refreshKey={snapshot.finished || null} />
        <div className={styles.demoHeading}>
            <strong>Предложения поставщиков на сегодня</strong>
            <div className={styles.priceHeadingActions}>
                <span>Запрошено: {snapshot.quantity || quantity} шт.</span>
                {loading && <span>Данные подгружаются…</span>}
                {tableExpanded && !loading && offers.length > 0 && <button className={`${styles.refreshPrices} ${completeOnly ? styles.activeOfferFilter : ""}`} type="button" aria-pressed={completeOnly} onClick={onToggleComplete}>С ценой и количеством</button>}
                {tableExpanded && !loading && (showAll || hasHiddenOffers) && <button className={styles.refreshPrices} type="button" onClick={onToggleAll}>{showAll ? "Показать топ-10" : `Показать все предложения (${filteredOffers.length})`}</button>}
                {!loading && snapshot.status !== "PROCESSING" && offers.length > 0 && canRequestSources && <button className={styles.refreshPrices} type="button" onClick={() => onRequest(product)}>Запросить цены</button>}
                {!loading && snapshot.status !== "PROCESSING" && !canRequestSources && <span>Все API-поставщики уже отвечали сегодня</span>}
                {(loading || offers.length > 0 || supplierResults.length > 0) && <button className={styles.refreshPrices} type="button" aria-expanded={tableExpanded} aria-controls={tableId} onClick={() => setExpandedProductKey(tableExpanded ? null : productKey)}>{tableExpanded ? "Свернуть предложения" : "Развернуть предложения"}</button>}
            </div>
        </div>
        <div id={tableId}>
        {tableExpanded && loading && <div className={styles.offerTable} role="table" aria-label="Предложения поставщиков загружаются">
            <div className={styles.offerHead} role="row"><span>Поставщик</span><span>В наличии</span><span>Цена / шт.</span><span>Условия цены</span><span>Срок поставки</span><span /></div>
            {PRICE_SUPPLIERS.map(item => <div className={styles.supplierResultRow} role="row" key={item.source_code}>
                <strong>{item.supplier_name}</strong><span>Данные подгружаются…</span><span /><span>—</span><span />
            </div>)}
        </div>}
        {tableExpanded && !loading && (offers.length > 0 || supplierResults.length > 0) && <OfferTable supplierResults={supplierResults} displayedOffers={displayedOffers} ariaLabel="Предложения поставщиков на сегодня" />}
        </div>
        {!loading && snapshot.status === "PROCESSING" && <div className={styles.priceRequestState}><span className={styles.loader} />Поставщики обрабатывают запрос…</div>}
        {!loading && snapshot.status !== "PROCESSING" && offers.length === 0 && <div className={styles.noOffers}>
            <div><strong>Сегодня предложений в базе нет</strong><span>{canRequestSources ? "Получить актуальные цены, остатки и сроки у ещё не ответивших поставщиков." : "Все включённые API-поставщики уже ответили, но предложений не вернули."}</span></div>
            {canRequestSources && <button className={styles.primaryButton} type="button" onClick={() => onRequest(product)}>Запросить цены</button>}
        </div>}
        {requestError && <p className={styles.error} role="alert">{requestError}</p>}
    </>;
}

function PopularPreview({ products, annotations, onAnnotation, materials, priceSnapshots, quantity, priceErrors, showAllOffers, setShowAllOffers, completeOffersOnly, setCompleteOffersOnly, onRequestPrices, featuredPart, onFeaturedPartChange, onVariantChange, loading, loadError }) {
    const available = products.slice(0, 10);
    const featured = available.find(product => product.part_number === featuredPart) || available[0];
    const remaining = available.filter(product => product.part_number !== featured?.part_number);
    return <section className={styles.popularPreview} aria-label="Популярные позиции">
        <div><strong>Популярные позиции</strong><span>10 самых частых позиций в последних 50 запросах</span></div>
        {loading && <div className={styles.popularLoading} role="status" aria-label="Загрузка данных"><span className={styles.loadingSpinner} aria-hidden="true" /></div>}
        {loadError && <p role="alert">Не удалось загрузить популярные позиции. Обновите страницу, чтобы повторить.</p>}
        {!loading && !loadError && !available.length && <p>Нет популярных позиций для выбранных фильтров.</p>}
        {featured && <article className={styles.popularFeatured}>
            <div className={styles.popularRank}><strong>Основная позиция</strong><span>Запросов: {featured.request_count ?? "—"}</span></div>
            {featured.catalog_variants?.length > 1 && <div className={styles.variantSelector}>
                <label>Вариант производителя
                    <select aria-label="Вариант производителя" value={featured.manufacturer}
                        onChange={event => onVariantChange(featured.part_number, event.target.value)}>
                        {featured.catalog_variants.map(variant => <option key={variant.manufacturer} value={variant.manufacturer}>{variant.manufacturer}</option>)}
                    </select>
                </label>
            </div>}
            {featured.catalog_details_status && featured.catalog_details_status !== "loaded" && <p role="status">{
                featured.catalog_details_status === "ambiguous" ? "Найдено несколько вариантов карточки. Уточните производителя через поиск."
                    : featured.catalog_details_status === "unavailable" ? "Не удалось загрузить сохранённую карточку. Обновите страницу, чтобы повторить."
                        : "Сохранённая карточка для этого артикула и производителя пока не найдена."
            }</p>}
            {!featured.catalog_details_status
                ? <div className={styles.popularLoading} role="status" aria-label="Загрузка карточки"><span className={styles.loadingSpinner} aria-hidden="true" /></div>
                : <ProductDetails product={featured} materials={materials[featured.part_number] || []} onAnnotation={onAnnotation} />}
            <div className={styles.expanded}>
                <ProductPricePanel product={featured} snapshot={priceSnapshots[featured.part_number] || {}} quantity={quantity}
                    requestError={priceErrors[featured.part_number]} showAll={Boolean(showAllOffers[featured.part_number])} completeOnly={completeOffersOnly[featured.part_number] !== false}
                    onToggleAll={() => setShowAllOffers(current => ({ ...current, [featured.part_number]: !current[featured.part_number] }))}
                    onToggleComplete={() => { setCompleteOffersOnly(current => ({ ...current, [featured.part_number]: current[featured.part_number] === false })); setShowAllOffers(current => ({ ...current, [featured.part_number]: false })); }}
                    onRequest={onRequestPrices} />
            </div>
        </article>}
        <div className={styles.popularList}>{remaining.map((product, index) => <article className={styles.productCard} key={product.id || product.part_number}>
            <div className={styles.productSummary} role="button" tabIndex="0" onClick={() => onFeaturedPartChange(product.part_number)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onFeaturedPartChange(product.part_number); } }}>
                <span className={styles.popularPosition}>{index + 2}</span>
                <div className={styles.productVisual}><ProductImage partNumber={product.part_number} materials={materials[product.part_number] || []} /></div>
                <div className={styles.productIdentity}>
                    <div className={styles.productTitleRow}><strong>{product.part_number}</strong></div>
                    <span>{product.manufacturer || "Производитель не указан"}</span>
                                <CardQualityBadge product={withCharacteristicDisplay(product, annotations[product.id])} />
                    <p>{translatedCategory(withCharacteristicDisplay(product, annotations[product.id]))}</p>
                    <div className={styles.parameterLine}>{visibleParameters(withCharacteristicDisplay(product, annotations[product.id])).slice(0, 5).map(parameter => <span key={parameter.code}>{translatedParameter(parameter)}</span>)}</div>
                </div>
                <div className={styles.availability}><span className={styles.statusDot} />Запросов: {product.request_count ?? "—"}</div>
                <button className={styles.expandButton} type="button" aria-label={`Сделать основной ${product.part_number}`} onClick={event => { event.stopPropagation(); onFeaturedPartChange(product.part_number); }}><ChevronUp size={20} /></button>
            </div>
        </article>)}</div>
    </section>;
}

export default function CatalogPrototypePage({ catalogSearch = window.location.search, catalogView, onViewChange, onClearSelection } = {}) {
    const [localView, setLocalView] = useState("filters");
    const view = catalogView || localView;
    const [browseCategories, setBrowseCategories] = useState(true);
    const changeView = value => { setBrowseCategories(true); onViewChange ? onViewChange(value) : setLocalView(value); };
    const selectionParams = new URLSearchParams(catalogSearch);
    const selectionKey = JSON.stringify([selectionParams.get("productId"), selectionParams.get("part"), selectionParams.get("manufacturer")]);
    const searchGeneration = useRef(0);
    const [query, setQuery] = useState("");
    const [searchSettings,setSearchSettings] = useState(null);
    const [searchDictionary,setSearchDictionary] = useState(null);
    const lastSearchPlan=useRef(null);
    const [lastSearchQuery, setLastSearchQuery] = useState("");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [cardAnnotations, setCardAnnotations] = useState({});
    const updateCardAnnotation = useCallback((id, data) => setCardAnnotations(current => ({ ...current, [id]: data })), []);
    const [featuredPart, setFeaturedPart] = useState(null);
    const [selected, setSelected] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(CATALOG[0].query);
    const [categoryTree, setCategoryTree] = useState(fallbackCategoryTree);
    const [categoryCount, setCategoryCount] = useState(CATALOG.length);
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [categoryFilter, setCategoryFilter] = useState("");
    const [dictionaryManufacturers, setDictionaryManufacturers] = useState([]);
    const [allManufacturers, setAllManufacturers] = useState(false);
    const [searchMode, setSearchMode] = useState("auto");
    const [manufacturerFilter, setManufacturerFilter] = useState("");
    const [packageFilter, setPackageFilter] = useState("");
    const [supplierFilter, setSupplierFilter] = useState("");
    const matchesOption = (value, query) => value.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
    const [selectedManufacturers, setSelectedManufacturers] = useState([]);
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [quantity, setQuantity] = useState(100);
    const [popularProducts, setPopularProducts] = useState([]);
    const [popularReady, setPopularReady] = useState(false);
    const [popularError, setPopularError] = useState(false);
    const [priceRevision, setPriceRevision] = useState(0);
    const [priceSnapshots, setPriceSnapshots] = useState({});
    const [priceErrors, setPriceErrors] = useState({});
    const [showAllOffers, setShowAllOffers] = useState({});
    const [completeOffersOnly, setCompleteOffersOnly] = useState({});

    const products = useMemo(() => groupProducts(result?.items), [result]);
    const materialCandidates = useMemo(() => products.length ? products : popularProducts.slice(0, 10), [products, popularProducts]);
    const availabilityRows = useMemo(() => materialCandidates.map(product => ({ name: product.part_number })), [materialCandidates]);
    const { materials, error: materialsError, retry: retryMaterials } = useCatalogAssetAvailability(availabilityRows, materialCandidates.length > 0);
    const manufacturers = useMemo(() => [...new Set((result ? result.manufacturers ?? [] : dictionaryManufacturers).filter(Boolean))].sort(), [result, dictionaryManufacturers]);
    const matchingManufacturers = manufacturers.filter(value => matchesOption(value, manufacturerFilter));
    const visibleManufacturers = allManufacturers || manufacturerFilter.trim() ? matchingManufacturers : matchingManufacturers.filter((value, index) => index < 8 || selectedManufacturers.includes(value));
    const packageOptions = useMemo(() => [...new Set(products.flatMap(product => visibleParameters(product)
        .filter(isPackageParameter)
        .map(parameter => parameter.value_ru || parameter.raw)
        .filter(value => value && value !== "—")))].sort((left, right) => left.localeCompare(right, "ru", { numeric: true })), [products]);
    const matchesFilters = product => {
        if (selectedManufacturers.length && !selectedManufacturers.includes(product.manufacturer)) return false;
        if (selectedSuppliers.length && !product.suppliers.some(value => selectedSuppliers.includes(String(value).toLowerCase().replace(/\s+/g, "")))) return false;
        if (selectedPackages.length && !visibleParameters(product).filter(isPackageParameter).some(parameter => selectedPackages.includes(parameter.value_ru) || selectedPackages.includes(parameter.raw))) return false;
        return true;
    };
    const visible = products.filter(matchesFilters);
    const visiblePopular = popularProducts.filter(matchesFilters);
    const displayed = [...visible].sort((left, right) => Number(right.id === expanded) - Number(left.id === expanded));
    const activePricePart = result
        ? products.find(product => product.id === expanded)?.part_number
        : popularReady ? (visiblePopular.find(product => product.part_number === featuredPart) || visiblePopular[0])?.part_number : null;
    const activePriceLoaded = Boolean(activePricePart && priceSnapshots[activePricePart]?._loaded);
    const displayCategoryTree = useMemo(() => hideCategoryCounts(categoryTree), [categoryTree]);
    const filteredCategories = filterCategoryTree(displayCategoryTree, categoryFilter);

    useEffect(() => {
        let cancelled = false;
        const apply = items => {
            if (cancelled || !Array.isArray(items)) return;
            setPopularProducts(current => items.map((item, index) => {
                const existing = current.find(row => row.part_number === item.part_number);
                // Preserve an already selected manufacturer.
                if (existing?.catalog_details_status === "loaded") return existing;
                return { ...item, id: item.id || `popular-${index}-${item.part_number}`,
                    suppliers: item.suppliers || [], parameters: item.parameters || [] };
            }));
        };
        popularCatalogComponents().then(apply)
            .catch(() => { if (!cancelled) setPopularError(true); })
            .finally(() => { if (!cancelled) setPopularReady(true); });
        return () => { cancelled = true; };
    }, []);

    const showingPopular = !result && popularReady;
    useEffect(() => {
        if (!showingPopular) return;
        let cancelled = false;
        const pending = popularProducts.slice(0, 10).filter(product => !product.catalog_details_status);
        const worker = async () => {
            while (!cancelled && pending.length) {
                const product = pending.shift();
                const card = await popularCatalogDetails(product);
                if (!cancelled) setPopularProducts(current => current.map(row =>
                    row.part_number === product.part_number && !row.catalog_details_status ? card : row));
            }
        };
        void worker(); void worker();
        return () => { cancelled = true; };
        // Run once per visit; individual card updates must not restart pending requests.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showingPopular]);

    const summaryCandidates = result || materialCandidates.every(product => product.catalog_details_status) ? materialCandidates : [];
    const summaryIds = JSON.stringify(summaryCandidates.map(product => product.id)
        .filter(id => id && !String(id).startsWith('popular-')));
    useEffect(() => {
        if (!hasRole('ADMIN')) return;
        let cancelled = false;
        const pending = [...new Set(summaryCandidates.filter(needsCharacteristicDetails).map(product => product.id)
            .filter(id => id && !String(id).startsWith('popular-')))];
        const worker = async () => {
            while (!cancelled && pending.length) {
                const id = pending.shift();
                try {
                    const data = await productCharacteristics(id);
                    if (!cancelled) updateCardAnnotation(id, data);
                } catch { /* Keep saved catalog fields if annotation is unavailable. */ }
            }
        };
        void worker(); void worker();
        return () => { cancelled = true; };
        // summaryIds changes when catalog details finish loading.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [summaryIds, updateCardAnnotation]);

    useEffect(() => {
        catalogDictionary().then(dictionary => {
            setSearchDictionary(dictionary);
            setDictionaryManufacturers(dictionary.manufacturers || []);
            const labels = dictionary.category_labels || {};
            const tree = dictionary.category_tree?.length ? dictionary.category_tree : (dictionary.categories || []).map(category => ({ name: category, label: labels[category] || category, count: null, children: [] }));
            if (tree.length) {
                setCategoryTree(tree);
                setCategoryCount((dictionary.categories || []).length);
                const selectedPath = findCategoryPath(tree, CATALOG[0].query);
                if (selectedPath) setExpandedCategories(new Set(selectedPath.slice(0, -1).map((_, index) => categoryNodeKey(selectedPath.slice(0, index + 1)))));
            }
        }).catch(() => { /* встроенный короткий список остаётся резервом */ });
    }, []);
    useEffect(() => { Promise.resolve(catalogSettings()).then(setSearchSettings).catch(() => {}); }, []);

    useEffect(() => {
        const part = activePricePart;
        if (!part) return undefined;
        const controller = new AbortController();
        setPriceSnapshots(current => current[part]?._loaded ? current : { ...current, [part]: { _loaded: false } });
        const load = async () => {
            const snapshots = await catalogPriceSnapshots([part]);
            if (!controller.signal.aborted) setPriceSnapshots(current => ({ ...current, [part]: { ...(snapshots[part] || {}), _loaded: true } }));
        };
        if (!activePriceLoaded) load().catch(() => {
            if (!controller.signal.aborted) setPriceErrors(current => ({ ...current, snapshots: "Не удалось получить сегодняшние цены" }));
        });
        return () => controller.abort();
    }, [activePricePart, activePriceLoaded, priceRevision]);

    const runSearch = useCallback(async (searchText, options = {}) => {
        const plan=options.plan || (options.append ? lastSearchPlan.current : null);
        if (!searchText.trim() && !options.manufacturers?.length && !plan) return;
        setBrowseCategories(false);
        const generation = ++searchGeneration.current;
        const append = Boolean(options.append);
        append ? setLoadingMore(true) : setBusy(true);
        setError("");
        if (!append) {
            setSelected([]);
            setSelectedManufacturers(options.manufacturers || []); setSelectedSuppliers([]); setSelectedPackages([]);
            if (!options.keepManufacturerSearch) { setManufacturerFilter(""); setAllManufacturers(false); }
            setPackageFilter(""); setSupplierFilter(""); setCategoryFilter("");
            setSelectedCategory(options.category || "");
            setPriceSnapshots({}); setPriceErrors({}); setPriceRevision(value => value + 1);
        }
        if (!append) { setLastSearchQuery(searchText.trim()); setSearchMode(options.mode || "auto"); lastSearchPlan.current=plan; }
        try {
            let data = await searchCatalog({ query: searchText.trim(), mode: options.mode || "auto", limit: PAGE_SIZE, offset: options.offset || 0, ...(options.manufacturers?.length ? { manufacturers: options.manufacturers } : {}), ...(plan ? {plan} : {}) });
            if (generation !== searchGeneration.current) return;
            const engineChanged = append && options.currentEngine && data.engine !== options.currentEngine;
            if (engineChanged) {
                data = await searchCatalog({query: searchText.trim(), mode: options.mode || "auto", limit: PAGE_SIZE, offset: 0, ...(options.manufacturers?.length ? {manufacturers: options.manufacturers} : {}), ...(plan ? {plan} : {})});
                if (generation !== searchGeneration.current) return;
            }
            if (!append) lastSearchPlan.current=plan || data.plan || null;
            const combined = append && !engineChanged ? { ...data, items: [...(options.currentItems || []), ...(data.items || [])], offset: 0 } : data;
            setResult(previous => append ? { ...combined, possible_items: previous?.possible_items || [] } : combined);
            const grouped = groupProducts(data.items);
            if (!append) {
                const match = options.manufacturer ? grouped.find(item => item.manufacturer === options.manufacturer) : grouped[0];
                setExpanded(match?.id || grouped[0]?.id || null);
                if (match && options.manufacturer) setSelectedManufacturers([options.manufacturer]);
            }
        } catch (searchError) {
            if (generation === searchGeneration.current) setError(searchError.message || "Не удалось выполнить поиск");
        } finally {
            if (generation === searchGeneration.current) { append ? setLoadingMore(false) : setBusy(false); }
        }
    }, []);

    const runSelectionSearch = useRef(runSearch);
    runSelectionSearch.current = runSearch;
    useEffect(() => {
        const [productId, part, manufacturer] = JSON.parse(selectionKey);
        if (productId) {
            setBrowseCategories(false);
        const generation = ++searchGeneration.current;
            let active = true;
            setBusy(true); setError("");
            setSelectedManufacturers([]); setSelectedSuppliers([]); setSelectedPackages([]); setSelectedCategory("");
            catalogCard(productId).then(card => {
                if (!active || generation !== searchGeneration.current) return;
                setQuery(card.part_number); setLastSearchQuery(card.part_number); setSearchMode("part");
                setResult({ items: [card], total: 1, manufacturers: [card.manufacturer].filter(Boolean) });
                setExpanded(groupProducts([card])[0]?.id || null);
            }).catch(e => {
                if (!active || generation !== searchGeneration.current) return;
                setResult({ items: [], total: 0 }); setError(e.message || "Карточка недоступна");
            }).finally(() => { if (active && generation === searchGeneration.current) setBusy(false); });
            return () => { active = false; };
        }
        if (!part) return;
        setQuery(part);
        runSelectionSearch.current(part, { mode: "part", manufacturer: manufacturer || "" });
    }, [selectionKey]);

    const chooseManufacturer = value => {
        const next = selectedManufacturers.includes(value) ? selectedManufacturers.filter(item => item !== value) : [...selectedManufacturers, value];
        setSelectedManufacturers(next);
        if (!lastSearchQuery && !next.length && !lastSearchPlan.current) { setResult(null); return; }
        runSearch(lastSearchQuery, { manufacturers: next, mode: searchMode, keepManufacturerSearch: true, plan:lastSearchPlan.current });
    };

    const resetSearch = () => {
        searchGeneration.current += 1;
        setQuery(""); setLastSearchQuery(""); lastSearchPlan.current=null; setResult(null); setError("");
        setBusy(false); setLoadingMore(false); setExpanded(null); setFeaturedPart(null);
        setSelected([]); setSelectedCategory(""); setSearchMode("auto");
        setSelectedManufacturers([]); setSelectedSuppliers([]); setSelectedPackages([]);
        setManufacturerFilter(""); setPackageFilter(""); setSupplierFilter(""); setCategoryFilter("");
        setAllManufacturers(false);
        if (onClearSelection) { onClearSelection(); return; }
        const url = new URL(window.location.href);
        url.searchParams.delete("part"); url.searchParams.delete("manufacturer"); url.searchParams.delete("productId");
        window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    };

    const run = event => {
        event?.preventDefault();
        runSearch(query);
    };

    const chooseCategory = category => {
        setBrowseCategories(false);
        setSelectedCategory(category.query);
        setQuery(category.label);
        setSelectedManufacturers([]);
        setSelectedSuppliers([]);
        setSelectedPackages([]);
        runSearch(category.query, { category: category.query });
    };

    const toggle = (setter, value) => setter(current => current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value]);

    const toggleSelected = partNumber => setSelected(current => current.includes(partNumber)
        ? current.filter(item => item !== partNumber)
        : current.length < 4 ? [...current, partNumber] : current);

    const requestPrices = async product => {
        const part = product.part_number;
        setPriceErrors(current => ({ ...current, [part]: "" }));
        try {
            let snapshot = { ...(await requestCatalogPrices({ partNumber: part, manufacturer: product.manufacturer || "", quantity })), _loaded: true };
            setPriceSnapshots(withSnapshot(part, snapshot));
            for (let attempt = 0; attempt < 90 && snapshot.status === "PROCESSING"; attempt += 1) {
                await new Promise(resolve => window.setTimeout(resolve, 2000));
                snapshot = { ...(await catalogPriceRequest(snapshot.id)), _loaded: true };
                setPriceSnapshots(withSnapshot(part, snapshot));
            }
        } catch (requestError) {
            setPriceErrors(current => ({ ...current, [part]: requestError.message || "Не удалось запросить цены" }));
        }
    };

    const viewControls = <div className={styles.filterTitle}>
        <nav className={styles.viewSwitch} aria-label="Вид поиска номенклатуры">
            <button type="button" title="Фильтры" aria-label="Фильтры" aria-pressed={view === "filters"} onClick={() => changeView("filters")}><SlidersHorizontal size={18} aria-hidden="true" /></button>
            <button type="button" title="Категории" aria-label="Категории" aria-pressed={view === "categories"} onClick={() => changeView("categories")}><LayoutGrid size={18} aria-hidden="true" /></button>
        </nav>
        <strong>Каталог и фильтры</strong>
    </div>;

    return <div className={styles.page}>
        <section className={styles.searchPanel}>
            <div><h2>Поиск электронных компонентов</h2><p>Номенклатура, характеристики, цены и сроки поставки</p></div>
            <form className={styles.searchForm} onSubmit={run}>
                <label className={styles.searchBox}>
                    <Search size={19} />
                    <input aria-label="Поиск в пробном каталоге" value={query} onChange={event => setQuery(event.target.value)} placeholder={`Например: ${SEARCH_EXAMPLE}`} />
                </label>

                <button className={styles.primaryButton} disabled={busy || !query.trim()}>{busy ? "Поиск" : "Найти"}</button>
                <button type="button" className={styles.resetSearchButton} onClick={resetSearch}>Сбросить фильтр</button>
            </form>
            {searchSettings?.search_engine==='v3' && searchSettings?.interpret_provider==='none' && <StructuredCatalogSearch dictionary={searchDictionary} busy={busy} onSearch={plan=>runSearch('',{plan})}/>}
            <CatalogSearchTools result={result} className={styles.searchExplanation} />
            {result?.engine === "v3" && result.word_corrections?.length > 0 && <p role="status" className={styles.searchExplanation}>
                Исправлены опечатки: {result.word_corrections.map(change => `«${change.from}» → «${change.to}»`).join(', ')}.
            </p>}
            {error && <p className={styles.error} role="alert">{error}</p>}
            {result?.clarification && <p className={styles.clarification}>{result.clarification}</p>}

        </section>

        <div className={`${styles.catalogLayout} ${view === "categories" ? styles.directoryLayout : ""}`}>
            {view === "filters" && <aside className={styles.filters}>
                {viewControls}
                <section className={styles.filterSection}>
                    <h3>Категории</h3>
                    <div className={styles.catalogTree}>
                        <input className={styles.categorySearch} aria-label="Фильтр категорий" value={categoryFilter} onChange={event => setCategoryFilter(event.target.value)} placeholder={`Категорий: ${categoryCount}`} />
                        <div className={styles.categoryList}>{filteredCategories.map(category => <CategoryTreeNode key={category.name} node={category} path={[]} selected={selectedCategory} expanded={expandedCategories} forceOpen={Boolean(categoryFilter.trim())} onToggle={key => setExpandedCategories(current => { const next = new Set(current); next.has(key) ? next.delete(key) : next.add(key); return next; })} onSelect={chooseCategory} />)}</div>
                    </div>
                </section>
                {SHOW_SUPPLIER_FILTERS && <section className={styles.filterSection}>
                    <h3>Поставщики</h3>
                    <input className={styles.categorySearch} aria-label="Фильтр поставщиков" placeholder="Найти поставщика" value={supplierFilter} onChange={event => setSupplierFilter(event.target.value)} />
                    {SUPPLIER_FILTERS.filter(item => matchesOption(item.label, supplierFilter)).map(item => <label className={styles.checkRow} key={item.value}><input type="checkbox" checked={selectedSuppliers.includes(item.value)} onChange={() => toggle(setSelectedSuppliers, item.value)} />{item.label}</label>)}
                </section>}
                <section className={styles.filterSection}>
                    <h3>Производитель</h3>
                    <input className={styles.categorySearch} aria-label="Фильтр производителей" placeholder="Найти производителя" value={manufacturerFilter} onChange={event => setManufacturerFilter(event.target.value)} />
                    {manufacturers.length ? <div className={`${styles.packageOptions} ${styles.manufacturerOptions}`}>{visibleManufacturers.map(value => <label className={styles.checkRow} key={value}><input type="checkbox" checked={selectedManufacturers.includes(value)} disabled={busy} onChange={() => chooseManufacturer(value)} />{value}</label>)}</div> : <span className={styles.filterHint}>Производители не найдены</span>}
                    {!manufacturerFilter.trim() && manufacturers.length > 8 && <button type="button" className={styles.expandManufacturers} aria-expanded={allManufacturers} onClick={() => setAllManufacturers(value => !value)}>{allManufacturers ? "Свернуть" : `Показать ещё (${manufacturers.length - visibleManufacturers.length})`}{allManufacturers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>}
                    {manufacturers.length > 0 && !matchingManufacturers.length && <span className={styles.filterHint}>Совпадений нет</span>}
                </section>
                {packageOptions.length > 0 && <section className={styles.filterSection}>
                    <h3>Корпус</h3>
                    <input className={styles.categorySearch} aria-label="Фильтр корпусов" placeholder="Найти корпус" value={packageFilter} onChange={event => setPackageFilter(event.target.value)} />
                    <div className={styles.packageOptions}>{packageOptions.filter(value => matchesOption(value, packageFilter)).map(value => <label className={styles.checkRow} key={value}><input type="checkbox" checked={selectedPackages.includes(value)} onChange={() => toggle(setSelectedPackages, value)} />{value}</label>)}</div>
                </section>}
                {materialsError && <button className={styles.linkButton} type="button" onClick={retryMaterials}>Повторить загрузку материалов</button>}
            </aside>}

            <main className={styles.results}>
                {view === "categories" && viewControls}
                {view === "categories" && (browseCategories ? <CategoryDirectory tree={displayCategoryTree} onSelect={chooseCategory} />
                    : <button type="button" className={styles.resetSearchButton} onClick={() => setBrowseCategories(true)}>Выбрать другую категорию</button>)}
                {!result && !error && view === "filters" && <PopularPreview products={visiblePopular} annotations={cardAnnotations} onAnnotation={updateCardAnnotation} materials={materials}
                    priceSnapshots={priceSnapshots} quantity={quantity} priceErrors={priceErrors}
                    showAllOffers={showAllOffers} setShowAllOffers={setShowAllOffers} completeOffersOnly={completeOffersOnly} setCompleteOffersOnly={setCompleteOffersOnly} onRequestPrices={requestPrices}
                    featuredPart={featuredPart} onFeaturedPartChange={setFeaturedPart} loading={!popularReady} loadError={popularError}
                    onVariantChange={(part, manufacturer) => setPopularProducts(current => current.map(product => {
                        if (product.part_number !== part) return product;
                        const variant = product.catalog_variants?.find(row => row.manufacturer === manufacturer);
                        return variant ? { ...product, ...variant, part_number: product.part_number,
                            request_count: product.request_count, last_requested_at: product.last_requested_at } : product;
                    }))} />}
                {!busy && error && !result && <div className={styles.empty}>Не удалось загрузить каталог. Используйте кнопку «Найти» повторно.</div>}
                {result && !result.clarification && (view !== "categories" || !browseCategories) && <>
                <div className={styles.resultsToolbar}>
                    <div><h3>Найдено: {result.total}</h3><span>Показано уникальных позиций: {visible.length}</span></div>
                    <label>Количество<input type="number" min="1" value={quantity} onChange={event => setQuantity(Math.max(1, Number(event.target.value) || 1))} /><span>шт.</span></label>
                </div>

                {result.engine === "v3" && result.index_updating && <p role="status" className={styles.searchExplanation}>Поисковая база обновляется. Некоторые позиции временно не показаны; повторите поиск после обновления.</p>}
                {result.engine === "v3" && result.retrieval_mode && result.retrieval_mode !== "structured" && mayUseVectorRetrieval(result.retrieval_method) && result.embeddings_pending > 0 && <p role="status" className={styles.searchExplanation}>Смысловой индекс ещё наполняется. Часть совпадений пока ищется только по тексту.</p>}
                {result.engine === "v3" && result.plan && <div className={styles.searchConditions} aria-label="Условия поиска">
                    {result.plan.category && <span>Категория: {result.plan_labels?.category || result.plan.category}</span>}
                    {(result.plan.filters || []).map((filter, index) => <span key={index}>{conditionLabel(filter, result.filter_labels)}</span>)}
                    {result.plan.any_of?.length > 0 && <span>И дополнительно: {result.plan.any_of.map(group => '(' + group.map(filter => conditionLabel(filter, result.filter_labels)).join(' И ') + ')').join(' ИЛИ ')}</span>}
                </div>}
                {result.plan?.category_hint && <p className={styles.searchExplanation}>Предполагаемая категория не ограничивает результаты поиска.</p>}
                {!visible.length && <div className={styles.empty}>По выбранным фильтрам позиций нет.</div>}
                <div className={view === "categories" ? styles.productTiles : styles.productRows} aria-label="Номенклатура">
                {displayed.map(product => {
                    const isExpanded = expanded === product.id;
                    const summaryProduct = withCharacteristicDisplay(product, cardAnnotations[product.id]);
                    const productMaterials = materials[product.part_number] || [];
                    const snapshot = priceSnapshots[product.part_number] || {};
                    return <article className={`${styles.productCard} ${isExpanded ? styles.activeCard : ""}`} key={product.id}>
                        <div className={styles.productSummary} onClick={() => setExpanded(product.id)}>
                            <input aria-label={`Добавить ${product.part_number} к сравнению`} type="checkbox" checked={selected.includes(product.part_number)} onClick={event => event.stopPropagation()} onChange={() => toggleSelected(product.part_number)} />
                            <div className={styles.productVisual}><ProductImage partNumber={product.part_number} materials={productMaterials} /></div>
                            <div className={styles.productIdentity}>
                                <div className={styles.productTitleRow}><strong>{product.part_number}</strong>{product.product_url && <a href={product.product_url} target="_blank" rel="noopener noreferrer" title="Карточка поставщика" onClick={event => event.stopPropagation()}><ExternalLink size={15} /></a>}</div>
                                <span>{product.manufacturer || "Производитель не указан"}</span>
                                <CardQualityBadge product={summaryProduct} />
                                <p>{translatedCategory(summaryProduct)}</p>
                                <div className={styles.parameterLine}>{visibleParameters(summaryProduct).slice(0, 5).map(parameter => <span key={parameter.code}>{translatedParameter(parameter)}</span>)}</div>
                            </div>
                            <button className={styles.expandButton} type="button" onClick={event => { event.stopPropagation(); setExpanded(isExpanded ? null : product.id); }} aria-expanded={isExpanded}>{isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</button>
                        </div>
                        {isExpanded && <div className={styles.expanded}>
                            <ProductDetails product={product} materials={productMaterials} onAnnotation={updateCardAnnotation} />
                            <ProductPricePanel product={product} snapshot={snapshot} quantity={quantity}
                                requestError={priceErrors[product.part_number]} showAll={Boolean(showAllOffers[product.part_number])}
                                completeOnly={completeOffersOnly[product.part_number] !== false}
                                onToggleAll={() => setShowAllOffers(current => ({ ...current, [product.part_number]: !current[product.part_number] }))}
                                onToggleComplete={() => { setCompleteOffersOnly(current => ({ ...current, [product.part_number]: current[product.part_number] === false })); setShowAllOffers(current => ({ ...current, [product.part_number]: false })); }}
                                onRequest={requestPrices} />
                        </div>}
                    </article>;
                })}
                </div>
                {result.identity_items?.length > 0 && <section className={styles.possibleMatches} aria-label="Артикул найден, условия не подтверждены">
                    <h3>Артикул найден, дополнительные условия не подтверждены</h3>
                    <p>Эти карточки не вошли в результаты: указанные производитель, категория или характеристики не совпадают либо не заполнены. Открыть карточку можно отдельно по артикулу.</p>
                    {groupProducts(result.identity_items).map(product => <article key={product.id}>
                        <button type="button" onClick={() => { setQuery(product.part_number); runSearch(product.part_number, { mode: "part", manufacturer: product.manufacturer }); }}>{product.part_number}</button>
                        <span>{product.manufacturer}</span>
                        <p>{product.description}</p>
                    </article>)}
                </section>}
                {result.possible_items?.length > 0 && <section className={styles.possibleMatches} aria-label="Возможные совпадения">
                    <h3>Возможные совпадения — характеристики требуют проверки</h3>
                    <p>Часть значений найдена только в описании поставщика. Эти позиции не входят в количество подтверждённых результатов.</p>
                    {groupProducts(result.possible_items).map(product => <article key={product.id}>
                        <button type="button" onClick={() => { setQuery(product.part_number); runSearch(product.part_number, { mode: "part", manufacturer: product.manufacturer }); }}>{product.part_number}</button>
                        <span>{product.manufacturer} · {supplierName(product.supplier)}</span>
                        <p>{product.description}</p>
                        <small>Только в описании: {(product.match_evidence || []).map(item => `${result.filter_labels?.[item.code] || item.code}: ${item.text}`).join("; ")}</small>
                    </article>)}
                </section>}
                {(result.items || []).length < result.total && <button className={styles.loadMore} type="button" disabled={loadingMore} onClick={() => runSearch(lastSearchQuery, { manufacturers: selectedManufacturers, mode: searchMode, append: true, offset: result.items.length, currentItems: result.items, currentEngine: result.engine })}>{loadingMore ? "Загружаю…" : `Показать ещё (${result.total - result.items.length})`}</button>}
                </>}
            </main>
        </div>

        {selected.length > 0 && <div className={styles.compareBar}><span><BarChart3 size={18} />Выбрано для сравнения: <strong>{selected.length}</strong></span><div>{selected.map(item => <span key={item}>{item}</span>)}</div><button type="button" disabled={selected.length < 2}>Сравнить</button></div>}
    </div>;
}
